import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Calendar, Sparkles } from "lucide-react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
};
const dayName = (d) => new Date(d).toLocaleDateString("en-US", { weekday: "long" });

const COLOR_PRESETS = [
  "#AF3C3C", "#f97316", "#ec4899", "#a855f7", "#3b82f6", "#16a34a",
  "#dc2626", "#a16207", "#0d9488", "#e11d48", "#2563eb",
];

const EMPTY = {
  name: "", holiday_type: "fixed", start_date: "", end_date: "",
  color: "#AF3C3C", description: "", is_active: true,
};

export default function AdminHolidays() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/holidays");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setEditing("new");
    setForm({ ...EMPTY });
  };
  const openEdit = (h) => {
    setEditing(h.id);
    setForm({
      name: h.name, holiday_type: h.holiday_type,
      start_date: h.start_date, end_date: h.end_date,
      color: h.color || "#AF3C3C",
      description: h.description || "", is_active: h.is_active !== false,
    });
  };
  const save = async () => {
    if (!form.name || !form.start_date) {
      toast.error("Name and start date are required");
      return;
    }
    setSubmitting(true);
    try {
      if (editing === "new") {
        await api.post("/holidays", form);
        toast.success("Holiday created");
      } else {
        await api.patch(`/holidays/${editing}`, form);
        toast.success("Holiday updated");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };
  const remove = async (h) => {
    if (!window.confirm(`Delete (deactivate) "${h.name}"?`)) return;
    try {
      await api.delete(`/holidays/${h.id}`);
      toast.success("Holiday deactivated");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const grouped = rows.reduce((acc, r) => {
    const y = r.year;
    acc[y] = acc[y] || { fixed: [], optional: [] };
    acc[y][r.holiday_type === "optional" ? "optional" : "fixed"].push(r);
    return acc;
  }, {});
  const years = Object.keys(grouped).sort();

  return (
    <div className="space-y-5" data-testid="admin-holidays">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">HR</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Holiday Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage Fixed and Optional company holidays.</p>
        </div>
        <Button onClick={openNew} className="brand-bg brand-bg-hover text-white" data-testid="new-holiday-btn">
          <Plus className="h-4 w-4 mr-2" /> New Holiday
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading...</div>
      ) : years.length === 0 ? (
        <div className="bg-white rounded-xl border border-red-100 p-8 text-center text-sm text-slate-500">
          No holidays configured.
        </div>
      ) : (
        years.map((year) => (
          <section key={year} className="bg-white rounded-xl border border-red-100 overflow-hidden">
            <div className="px-5 py-3 bg-red-50/50 border-b border-red-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">
                {year} <span className="text-slate-500 font-normal text-sm">
                  ({(grouped[year].fixed?.length || 0)} fixed · {(grouped[year].optional?.length || 0)} optional)
                </span>
              </h2>
            </div>
            <table className="w-full text-sm" data-testid={`holiday-table-${year}`}>
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Holiday</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Date(s)</th>
                  <th className="text-left px-4 py-2">Day</th>
                  <th className="text-left px-4 py-2">Description</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...grouped[year].fixed, ...grouped[year].optional].map((h) => (
                  <tr key={h.id} className="border-t border-slate-100" data-testid={`holiday-row-${h.id}`}>
                    <td className="px-4 py-2 flex items-center gap-2">
                      <div className="h-6 w-6 rounded text-white flex items-center justify-center text-xs font-bold flex-shrink-0"
                           style={{ background: h.color || "#AF3C3C" }}>{h.name[0]}</div>
                      <span className="font-medium">{h.name}</span>
                    </td>
                    <td className="px-4 py-2">
                      {h.holiday_type === "fixed" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded bg-red-100 text-red-800 border-red-200">
                          <Calendar className="h-3 w-3" /> Fixed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border-amber-200">
                          <Sparkles className="h-3 w-3" /> Optional
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {h.start_date === h.end_date ? fmtDate(h.start_date)
                                                    : `${fmtDate(h.start_date)} → ${fmtDate(h.end_date)}`}
                    </td>
                    <td className="px-4 py-2 text-slate-500">{dayName(h.start_date)}</td>
                    <td className="px-4 py-2 text-slate-600 max-w-[250px] truncate" title={h.description}>
                      {h.description || "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => openEdit(h)} className="p-1.5 rounded hover:bg-slate-100"
                              data-testid={`edit-holiday-${h.id}`} title="Edit">
                        <Edit2 className="h-4 w-4 text-slate-600" />
                      </button>
                      <button onClick={() => remove(h)} className="p-1.5 rounded hover:bg-red-50 text-red-700"
                              data-testid={`delete-holiday-${h.id}`} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New Holiday" : "Edit Holiday"}</DialogTitle>
            <DialogDescription>
              {editing === "new"
                ? "Create a new Fixed (mandatory) or Optional company holiday."
                : "Edit holiday details. Changes apply across all employees."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Diwali" className="mt-1.5" data-testid="holiday-name-input" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={form.holiday_type} onValueChange={(v) => setForm({ ...form, holiday_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed (Mandatory — all employees)</SelectItem>
                  <SelectItem value="optional">Optional (Choose-able by employee)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Start Date</Label>
                <Input type="date" value={form.start_date}
                       onChange={(e) => setForm({ ...form, start_date: e.target.value,
                                                  end_date: e.target.value > form.end_date ? e.target.value : form.end_date })}
                       className="mt-1.5" data-testid="holiday-start-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">End Date (for multi-day)</Label>
                <Input type="date" value={form.end_date || form.start_date}
                       onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                       min={form.start_date}
                       className="mt-1.5" data-testid="holiday-end-input" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Color (Optional)</Label>
              <div className="flex gap-2 mt-1.5 flex-wrap" data-testid="holiday-color-picker">
                {COLOR_PRESETS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                          className={`h-7 w-7 rounded border-2 ${form.color === c ? "border-slate-900" : "border-transparent"}`}
                          style={{ background: c }} />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Description</Label>
              <Textarea rows={2} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="(optional)" className="mt-1.5" data-testid="holiday-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={save} disabled={submitting} className="brand-bg brand-bg-hover text-white"
                    data-testid="holiday-save-btn">
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
